// Welfare resolution: what the placed structures actually provide, and how
// they degrade. This is where the daily checklist gets its teeth — a waterer
// that is full but filthy does not count (GAME-DESIGN.md §3).

import { clamp, round2 } from '../core/math';
import type { ChickenEntity, ChoreId, GameState, Structure } from '../core/types';
import { feedNeedScale, waterNeedScale } from './access';
import { feedDrainUnits, openFeedBag, rodentFeedLoss, rodentPressure } from './feed';
import { fountDrainMultiplier } from './gear';
import type { BiologyConfig } from './config';
import { effectsOf, getItem } from './items';
import { feedTypeForStage } from './lifecycle';
import { hourOfDay, isDaylight } from './time';
import type { EnvContext } from './physiology';
import { DISEASES, type BiosecurityContext } from './disease';
import { fixturesOf } from './spots';
import { stormAnchorDue } from './storm';
import { effectiveTempForSpot } from './microclimate';

export interface FacilitySummary {
  waterers: Structure[];
  feeders: Structure[];
  coops: Structure[];
  nestBoxes: Structure[];
  perches: Structure[];
  brooders: Structure[];
  hasShade: boolean;
  /** Aviary netting over the run — the only complete answer to a hawk. */
  hasOverheadNet: boolean;
  hasFootbath: boolean;
  /** Half-inch hardware cloth on the run. */
  hasHardwareCloth: boolean;
  hasCoveredFeeder: boolean;
  hasCoopLight: boolean;
  hasGrit: boolean;
  hasCalcium: boolean;
  coopCapacity: number;
  fenceHeightInches: number;
  anyDoorOpen: boolean;
}

export function summarizeFacilities(state: GameState): FacilitySummary {
  const has = (key: keyof ReturnType<typeof effectsOf>) =>
    state.structures.some((s) => Boolean(effectsOf(s)[key]));

  const fences = state.structures.filter((s) => effectsOf(s).fence);
  const coops = state.structures.filter((s) => effectsOf(s).coop);

  return {
    waterers: state.structures.filter((s) => effectsOf(s).providesWater),
    feeders: state.structures.filter((s) => effectsOf(s).providesFeed),
    coops,
    nestBoxes: state.structures.filter((s) => effectsOf(s).nestBox),
    perches: state.structures.filter((s) => effectsOf(s).perch),
    brooders: state.structures.filter((s) => effectsOf(s).brooder),
    hasShade: has('shade') || coops.length > 0,
    hasOverheadNet: has('overheadNet'),
    hasFootbath: has('footbath'),
    hasHardwareCloth: has('hardwareCloth'),
    hasCoveredFeeder: has('coveredFeeder'),
    hasCoopLight: has('coopLight'),
    // Consumables live in the inventory rather than on the grid.
    hasGrit: (state.inventory.grit ?? 0) > 0,
    hasCalcium: (state.inventory.oyster_shell ?? 0) > 0,
    coopCapacity: coops.reduce((sum, s) => sum + (effectsOf(s).capacity ?? 0), 0),
    // A perimeter is only as good as its lowest segment.
    fenceHeightInches: fences.length
      ? Math.min(...fences.map((s) => effectsOf(s).heightInches ?? 0))
      : 0,
    anyDoorOpen: coops.some((s) => s.state.doorOpen !== false),
  };
}

/** Clean, full water within reach. Fouled water does not count as water. */
export function availableWater(facilities: FacilitySummary): { has: boolean; purity: number } {
  const usable = facilities.waterers.filter((w) => (w.state.water ?? 0) > 5);
  if (usable.length === 0) return { has: false, purity: 0 };
  const purity = Math.max(...usable.map((w) => w.state.waterPurity ?? 100));
  return { has: purity > 35, purity };
}

export function availableFeed(
  facilities: FacilitySummary,
  chicken: ChickenEntity,
): { has: boolean; correctForStage: boolean } {
  const stocked = facilities.feeders.filter((f) => (f.state.feed ?? 0) > 5);
  if (stocked.length === 0) return { has: false, correctForStage: false };
  const wanted = feedTypeForStage(chicken.stage);
  return {
    has: true,
    correctForStage: stocked.some((f) => f.state.feedType === wanted),
  };
}

/** Above this the litter needs new bedding, not just a scoop. */
export const LITTER_REBED_THRESHOLD = 45;

/** Mean dirtiness across the tiles the birds actually live on. */
export function averageLitterDirtiness(state: GameState): number {
  if (state.grid.tiles.length === 0) return 0;
  const total = state.grid.tiles.reduce((sum, t) => sum + t.dirtinessLevel, 0);
  return round2(total / state.grid.tiles.length);
}

export function buildEnvContext(
  chicken: ChickenEntity,
  state: GameState,
  facilities: FacilitySummary,
  config: BiologyConfig,
  /**
   * The spot this bird is walking to for shade and can actually reach before
   * this step ends (PLAN-04 S3). Without it a bird is charged the full step at
   * the tile she is leaving, and a catch-up step then bills her three hours of
   * lethal sun for a walk she made in the first minute of it.
   */
  escapingTo?: string,
): EnvContext {
  const water = availableWater(facilities);
  const feed = availableFeed(facilities, chicken);
  const roosting = chicken.currentState === 'ROOSTING';
  const raining = state.weather.condition === 'RAIN' || state.weather.condition === 'STORM';
  const sheltered = roosting || facilities.hasShade;

  const capacity = facilities.coopCapacity || config.welfare.birdsPerCoopCapacityDefault;
  const overcrowdedBy = Math.max(0, state.flock.length - capacity);

  const brooded = chicken.stage === 'chick' && facilities.brooders.length > 0;
  const brooderTsi = brooded
    ? brooderTsiFor(chicken, facilities.brooders[0], state.weather.tempF, config)
    : undefined;

  // The spot this bird is charged for this step decides how thirsty she is
  // (NUT-11 counts heat, and heat is measured in shade at the weather source),
  // so the need scale is derived from the same temperature physiology sees.
  const tempF = effectiveTempForSpot(
    state,
    chicken.spotId ?? escapingTo,
    hourOfDay(state.simHour),
    config,
  );

  return {
    // Not the forecast: the forecast is shade air, and a bird standing in the
    // sun is carrying more than that (PLAN-04 S3, WTH-05). A bird with no spot
    // is in the open.
    tempF,
    // Under a roof or in the coop, rain is just weather.
    wetAndExposed: raining && !roosting && !facilities.hasShade,
    sheltered,
    hasWater: water.has,
    waterPurity: water.purity,
    // A full waterer is not the same as a drink: she still has to get to it
    // (PLAN-04 S4). A bird with no roll yet reads as having reached both, so a
    // save from before the access model does not start out looking neglected.
    waterAccess: chicken.access?.water,
    hasFeed: feed.has,
    feedAccess: chicken.access?.feed,
    waterNeedScale: waterNeedScale(chicken, tempF, config),
    feedNeedScale: feedNeedScale(chicken, config),
    feedCorrectForStage: feed.correctForStage,
    litterDirtiness: averageLitterDirtiness(state),
    overcrowdedBy,
    canForage:
      state.housing === 'backyard' && !roosting && state.weather.condition !== 'STORM',
    roosting,
    // A roost bar is a coop fixture now (REFOCUS §7); a perch item still counts.
    hasPerch: facilities.perches.length > 0 || fixturesOf(state).roost,
    hasGrit: facilities.hasGrit,
    isLaying: chicken.gender === 'hen' && chicken.stage === 'adult',
    brooderTsi,
  };
}

/**
 * How far above the age target a brooder may sit before it counts as too hot.
 *
 * A lamp adds heat and cannot remove it, so once ambient alone passes the
 * target curve — which in a Puerto Rico summer happens at about three weeks —
 * a symmetric |actual − target| made the stress index unsatisfiable even with
 * the lamp switched fully off, and it stayed unsatisfiable for the rest of the
 * chick's brooding. That flowed straight into permanent body-condition damage
 * and made `brooder_boss` unearnable. The asymmetry is also the true model:
 * being under the target is the dangerous direction, while a chick in a warm
 * yard is simply off heat early and walks away from the lamp.
 */
const BROODER_WARM_TOLERANCE_F = 10;

/**
 * Brooder ambient = outdoor temperature plus whatever the lamp adds, compared
 * against the age-appropriate target T(w) = 95 − 5w.
 */
export function brooderTsiFor(
  chicken: ChickenEntity,
  brooder: Structure,
  ambientF: number,
  config: BiologyConfig,
): number {
  const weeks = chicken.ageInDays / 7;
  const target =
    config.brooder.startTempF -
    config.brooder.dropPerWeekF * Math.min(weeks, config.brooder.weeksUntilAmbient);

  // If lampSetting is not explicitly configured, default to the setting that
  // matches the chick's target temperature for its age.
  let lamp = brooder.state.lampSetting;
  if (lamp === undefined) {
    const needed = Math.max(0, target - ambientF);
    lamp = Math.round(clamp((needed / 40) * 100, 0, 100));
  }

  // Lamp setting maps to up to 40°F of lift over ambient.
  const brooderTempF = ambientF + (lamp / 100) * 40;
  if (brooderTempF < target) return round2(target - brooderTempF);
  return round2(Math.max(0, brooderTempF - target - BROODER_WARM_TOLERANCE_F));
}

export function buildBiosecurityContext(
  state: GameState,
  facilities: FacilitySummary,
): BiosecurityContext {
  const contagiousUnquarantined = state.flock.filter(
    (c) =>
      c.quarantinedUntilHour === undefined &&
      c.conditions.some((cond) => DISEASES[cond.id].contagious),
  ).length;
  return {
    hasFootbath: facilities.hasFootbath,
    hasCoveredFeeder: facilities.hasCoveredFeeder,
    // Feed spills when a feeder runs dry and gets refilled carelessly.
    spilledFeed: facilities.feeders.some((f) => (f.state.feed ?? 0) <= 0),
    contagiousUnquarantined,
    // A yard with rats in it is a yard with a disease problem waiting, and it
    // is the same spilled-feed chain the wild birds arrive by (PLAN-04 S5).
    rodentPressure: rodentPressure(state),
  };
}

/**
 * Degrades facilities and litter over time — this is what creates the chores.
 *
 * Nothing in here is rounded, and that is deliberate. Every value below is an
 * accumulator moved by a slice of an hour, and a live Real-Time tick is 1/3600
 * of one: three birds drink 0.00067 of a waterer per tick, and a tile soils by
 * 0.0027. Passing those through round2 floored each of them to zero *before*
 * the next tick could add to it, so the value never moved off its starting
 * point. Real-Time mode — the mode built for daily habits — had no water drain,
 * no feed drain, no coop dirt and no litter at all, which is to say no chores.
 * Litter was dead in both modes.
 *
 * The 2dp rule in AGENT.md §3 is about presenting a stat, not about storing a
 * running total. Round at the point of display instead.
 */
export function stepFacilities(state: GameState, hours: number, config: BiologyConfig): void {
  const w = config.welfare;
  const raining = state.weather.condition === 'RAIN' || state.weather.condition === 'STORM';

  const hasAutoFeed = state.structures.some((s) => effectsOf(s).autoFeed);
  const hasAutoWater = state.structures.some((s) => effectsOf(s).autoWater);
  const hasAutoLock = state.structures.some((s) => effectsOf(s).autoLock);

  for (const structure of state.structures) {
    const effects = effectsOf(structure);
    if (effects.providesWater) {
      // A fount empties at whatever rate the birds using it actually drink at,
      // so a layer at a chick drinker runs it dry (PLAN-04 S6, INC-16's spec
      // is a drinker built for chicks).
      const drink =
        state.flock.length * 0.8 * hours * (hasAutoWater ? 0.5 : 1) * fountDrainMultiplier(state, structure, config);
      structure.state.water = clamp((structure.state.water ?? 0) - drink, 0, 100);
      structure.state.waterPurity = clamp(
        (structure.state.waterPurity ?? 100) - w.waterFoulingPerHour * hours * (hasAutoWater ? 0.5 : 1),
        0,
        100,
      );
    }
    if (effects.providesFeed) {
      // The ration, not a flat rate: a Silkie eats 65 g where a Rhode Island
      // Red eats 122.5 (BRD-18/BRD-17), and a hanging feeder wastes some of
      // what the flock needs (PLAN-04 S5, design; ECO-06 is unresolved).
      const eaten = feedDrainUnits(state, hours, hasAutoFeed, config) + rodentFeedLoss(state, hours, config);
      structure.state.feed = clamp((structure.state.feed ?? 0) - eaten, 0, 100);
    }
    if (effects.coop) {
      structure.state.dirtiness = clamp((structure.state.dirtiness ?? 0) + 0.5 * hours, 0, 100);
    }
  }

  // Coop Lock-Timer: once it is dusk, every lockable coop latches on its own.
  // The chore still exists (doors still open at dawn), but the evening lock is
  // one less thing to remember.
  if (hasAutoLock && !isDaylight(state.simHour, state.weather)) {
    for (const structure of state.structures) {
      if (effectsOf(structure).lockable) structure.state.doorOpen = false;
    }
  }

  // Litter soils per bird per hour, and twice as fast when it is wet.
  const soilRate =
    w.litterSoilPerBirdPerHour * state.flock.length * (raining ? w.rainLitterMultiplier : 1);
  const perTile = (soilRate * hours) / Math.max(state.grid.tiles.length, 1);
  for (const tile of state.grid.tiles) {
    tile.dirtinessLevel = clamp(tile.dirtinessLevel + perTile, 0, 100);
  }
}

// --- Chore actions (the player's side of the loop) ---

/**
 * Fills every drinker, and every reserve barrel with it.
 *
 * The barrel is not a drinker — the birds cannot use it — but it was also the
 * only water container in the game nothing could ever fill, so the storm
 * checklist's `stock_water` item was permanently unsatisfiable and the
 * `storm_ready` badge unearnable. Topping it up on the same chore is the right
 * model anyway: you fill the barrel while you are already at the hose.
 */
export function refillWater(state: GameState): number {
  let refilled = 0;
  for (const structure of state.structures) {
    const effects = effectsOf(structure);
    if (!effects.providesWater && !effects.waterReserve) continue;
    structure.state.water = 100;
    structure.state.waterPurity = 100;
    refilled++;
  }
  return refilled;
}

export function refillFeed(state: GameState, feedItemId: string): number {
  const item = getItem(feedItemId);
  if (!item?.effects.feedType) return 0;
  if ((state.inventory[feedItemId] ?? 0) <= 0) return 0;

  let refilled = 0;
  for (const structure of state.structures) {
    if (!effectsOf(structure).providesFeed) continue;
    structure.state.feed = 100;
    structure.state.feedType = item.effects.feedType;
    refilled++;
  }
  if (refilled === 0) return 0;

  // Opening the bag is what exposes the feed to the heat, so the bag and the
  // clock that goes with it start here and not at the purchase (PLAN-04 S5,
  // NUT-19). One bag fills the whole yard's feeders, as it always has.
  const bag = openFeedBag(state, feedItemId);
  for (const structure of state.structures) {
    if (effectsOf(structure).providesFeed) structure.state.bagId = bag?.id;
  }
  return refilled;
}

/**
 * Scoop the droppings, and lay fresh bedding if the litter has earned it.
 *
 * Scooping is free; a bale of shavings is only spent once the litter is soiled
 * past the point the HUD starts nagging about. This used to burn a bale on
 * every single call however clean the yard was, and because the daily checklist
 * needs the litter chore to have actually done something, a diligent player was
 * forced to re-bed spotless litter every day — eight Eggits against a fifteen
 * Eggit wage, which left almost no margin toward the first coop. It is also
 * just wrong: you do not strip clean bedding daily.
 */
export function cleanLitter(state: GameState): boolean {
  const needsBedding = averageLitterDirtiness(state) >= LITTER_REBED_THRESHOLD;
  if (!needsBedding) {
    for (const tile of state.grid.tiles) tile.dirtinessLevel = 0;
    for (const structure of state.structures) {
      if (effectsOf(structure).coop) structure.state.dirtiness = 0;
    }
    return true;
  }

  if ((state.inventory.wood_shavings ?? 0) <= 0) return false;
  state.inventory.wood_shavings -= 1;
  for (const tile of state.grid.tiles) tile.dirtinessLevel = 0;
  for (const structure of state.structures) {
    if (effectsOf(structure).coop) structure.state.dirtiness = 0;
  }
  return true;
}

export function lockCoops(state: GameState): number {
  let locked = 0;
  for (const structure of state.structures) {
    if (!effectsOf(structure).lockable) continue;
    structure.state.doorOpen = false;
    locked++;
  }
  return locked;
}

/** Which chores are currently outstanding — drives the glowing HUD chips. */
export function dueChores(state: GameState, config: BiologyConfig): ChoreId[] {
  const facilities = summarizeFacilities(state);
  const due: ChoreId[] = [];
  const water = availableWater(facilities);

  if (!water.has || facilities.waterers.some((w) => (w.state.water ?? 0) < 40)) due.push('water');
  if (facilities.feeders.some((f) => (f.state.feed ?? 0) < 40) || facilities.feeders.length === 0) {
    due.push('feed');
  }
  if (averageLitterDirtiness(state) >= LITTER_REBED_THRESHOLD) due.push('litter');
  if (facilities.nestBoxes.some((n) => (n.state.eggs?.length ?? 0) > 0)) due.push('eggs');
  // Locking up is only due once it is actually dusk — by the sim clock the
  // player can see, not by daylight at their real-world location.
  if (facilities.anyDoorOpen && !isDaylight(state.simHour, state.weather)) due.push('lock_coop');
  // The storm one is due the moment a watch goes up, in daylight or not: a
  // hurricane does not wait for the evening chore window.
  if (stormAnchorDue(state)) due.push('storm_anchor');

  void config;
  return due;
}

export const ALL_CHORES: ChoreId[] = ['water', 'feed', 'litter', 'eggs', 'lock_coop', 'storm_anchor'];

/**
 * The chores this yard can actually perform right now.
 *
 * Collecting eggs needs eggs, and locking up needs a coop with an open door. A
 * starting player has neither of those on day one: the starter flock is three
 * chicks and it lays nothing yet. Scoring the daily checklist against all five
 * chores therefore made it unreachable on day one, and the checklist is the
 * game's only reliable income — so anything that would unlock those two chores
 * could never be afforded. Requiring only what the yard can do breaks that
 * circle without paying anyone for nothing.
 */
export function applicableChores(state: GameState): ChoreId[] {
  const facilities = summarizeFacilities(state);
  const applicable: ChoreId[] = ['water', 'feed', 'litter'];
  if (facilities.nestBoxes.some((n) => (n.state.eggs?.length ?? 0) > 0)) applicable.push('eggs');
  if (facilities.anyDoorOpen) applicable.push('lock_coop');
  // Only while there is a loose coop to tie down. Once the work is done the
  // chore leaves the list, because the daily wage is scored against it and a
  // chore that stayed after it was finished would make the wage unreachable.
  if (stormAnchorDue(state)) applicable.push('storm_anchor');
  return applicable;
}
